/**
 * Tasks (FR-TSK-01 to FR-TSK-04).
 *
 * Small on purpose. The only interesting write is `convertToTransaction`, which has to
 * create a transaction and stamp the task in a way that cannot post the same intention
 * twice.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import type { DateKey } from '../core/dates';
import type { AccountTypeLookup } from '../core/rollups';
import {
  TaskError,
  nextSortOrder,
  toTransactionInput,
  validateTask,
  type Task,
} from '../core/tasks';
import { auditDelete, auditUpdate } from './audit';
import { db } from './firebase';
import { trackWrite } from './syncState';
import { createTransaction } from './transactions';

const COLLECTION = 'tasks';

function toTask(id: string, data: Record<string, unknown>): Task {
  return {
    id,
    text: (data.text as string) ?? '',
    amountPaise: (data.amountPaise as number) ?? null,
    dueDateKey: (data.dueDateKey as DateKey) ?? null,
    done: data.done === true,
    sortOrder: (data.sortOrder as number) ?? 0,
    transactionId: (data.transactionId as string) ?? null,
    createdFrom: (data.createdFrom as Task['createdFrom']) ?? 'manual',
    note: (data.note as string) ?? null,
  };
}

export function watchTasks(onChange: (tasks: Task[]) => void): () => void {
  return onSnapshot(
    collection(db, COLLECTION),
    (snapshot) => onChange(snapshot.docs.map((d) => toTask(d.id, d.data()))),
    () => onChange([]),
  );
}

export async function saveTask(
  task: Partial<Task> & { text: string },
  existing: Task[],
  by: string,
): Promise<string> {
  const problem = validateTask({ text: task.text, amountPaise: task.amountPaise ?? null });
  if (problem) throw new TaskError(problem);

  const ref = task.id ? doc(db, COLLECTION, task.id) : doc(collection(db, COLLECTION));

  await trackWrite(
    setDoc(
      ref,
      {
        text: task.text.trim(),
        amountPaise: task.amountPaise ?? null,
        dueDateKey: task.dueDateKey ?? null,
        done: task.done ?? false,
        sortOrder: task.sortOrder ?? nextSortOrder(existing),
        transactionId: task.transactionId ?? null,
        createdFrom: task.createdFrom ?? 'manual',
        note: task.note ?? null,
        updatedAt: serverTimestamp(),
        ...(task.id ? {} : { createdAt: serverTimestamp(), enteredBy: by }),
      },
      { merge: Boolean(task.id) },
    ),
  );

  return ref.id;
}

export async function setDone(task: Task, done: boolean, by: string): Promise<void> {
  await trackWrite(
    updateDoc(doc(db, COLLECTION, task.id), { done, updatedAt: serverTimestamp() }),
  );
  await auditUpdate(COLLECTION, task.id, { done: task.done }, { done }, by);
}

export async function deleteTask(task: Task, by: string): Promise<void> {
  // Audit first, so the record of the deletion outlives the record (FR-DEL-03).
  await auditDelete(COLLECTION, task.id, task as unknown as Record<string, unknown>, by);
  await trackWrite(deleteDoc(doc(db, COLLECTION, task.id)));
}

/** Apply a reordering. Batched, because a renumber touches every row at once. */
export async function reorder(tasks: Task[]): Promise<void> {
  const batch = writeBatch(db);
  for (const task of tasks) {
    batch.update(doc(db, COLLECTION, task.id), { sortOrder: task.sortOrder });
  }
  await trackWrite(batch.commit());
}

/**
 * Turn a task into a transaction (FR-TSK-02).
 *
 * The transaction is created first, then the task is stamped with its id — which is what
 * `core/tasks.ts` checks to refuse a second posting. Failing between the two leaves a
 * transaction and an unstamped task, which is visible and correctable; the reverse would
 * mark the task posted with nothing behind it.
 *
 * The amount is a parameter rather than read off the task: a task is an intention written
 * down in advance, so its figure is an estimate for the same reason a recurring item's is.
 */
export async function convertToTransaction(
  task: Task,
  overrides: { accountId: string; dateKey: DateKey; amountPaise?: number },
  by: string,
  accountType: AccountTypeLookup,
): Promise<string> {
  const input = toTransactionInput(task, overrides);
  if (overrides.amountPaise !== undefined) input.amountPaise = overrides.amountPaise;

  const transactionId = await createTransaction(input, by, accountType);

  await trackWrite(
    updateDoc(doc(db, COLLECTION, task.id), {
      transactionId,
      done: true,
      updatedAt: serverTimestamp(),
    }),
  );

  return transactionId;
}

/**
 * Seed tasks from the agreed draft (FR-TSK-03).
 *
 * The workbook's commitments block holds one-off intentions alongside monthly
 * recurrences — CCTV camera, a name board, Gym PT. Which is which is the user's call, made
 * in `RECURRING-DRAFT.md`; this writes whatever that says.
 */
export async function seedTasks(texts: Omit<Task, 'id' | 'sortOrder'>[], by: string): Promise<number> {
  const existing = await getDocs(collection(db, COLLECTION));
  if (!existing.empty) {
    throw new TaskError(`Tasks already exist (${existing.size}).`);
  }

  const batch = writeBatch(db);
  texts.forEach((task, index) => {
    batch.set(doc(collection(db, COLLECTION)), {
      ...task,
      sortOrder: (index + 1) * 1000,
      createdFrom: 'workbook',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      enteredBy: by,
    });
  });
  await trackWrite(batch.commit());

  return texts.length;
}
